/**
 * Push Notification Service
 * Handles browser push notifications for order status, promotions, etc.
 */

class NotificationService {
  constructor() {
    this.permission = null;
    this.swRegistration = null;
    this.subscription = null;
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
  }

  _urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        await this.registerServiceWorker();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Check if permission is granted
   */
  hasPermission() {
    if (!this.isSupported) return false;
    return Notification.permission === 'granted';
  }

  /**
   * Register service worker for push notifications
   */
  async registerServiceWorker() {
    if (!this.isSupported) return false;

    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        this.swRegistration = registration;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return false;
    }
  }

  /**
   * Get or create a Push subscription using the app server VAPID public key.
   * Returns the subscription object (JSON-serializable).
   */
  async getOrCreatePushSubscription(vapidPublicKey) {
    if (!this.isSupported) return null;
    if (!this.hasPermission()) return null;

    if (!vapidPublicKey || !String(vapidPublicKey).trim()) {
      console.warn('Missing VAPID public key (REACT_APP_VAPID_PUBLIC_KEY). Push subscription not created.');
      return null;
    }

    if (!this.swRegistration) {
      await this.registerServiceWorker();
    }
    if (!this.swRegistration?.pushManager) return null;

    const existing = await this.swRegistration.pushManager.getSubscription();
    if (existing) {
      this.subscription = existing;
      return existing.toJSON ? existing.toJSON() : existing;
    }

    const created = await this.swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this._urlBase64ToUint8Array(String(vapidPublicKey).trim())
    });
    this.subscription = created;
    return created.toJSON ? created.toJSON() : created;
  }

  /**
   * Unsubscribe from browser push.
   */
  async unsubscribePush() {
    if (!this.isSupported) return { unsubscribed: false };
    try {
      if (!this.swRegistration) {
        await this.registerServiceWorker();
      }
      const sub = await this.swRegistration?.pushManager?.getSubscription();
      if (!sub) return { unsubscribed: true, hadSubscription: false };
      await sub.unsubscribe();
      this.subscription = null;
      return { unsubscribed: true, hadSubscription: true };
    } catch (e) {
      return { unsubscribed: false, error: e?.message || String(e) };
    }
  }

  /**
   * Show a notification
   * @param {Object} options - Notification options
   * @param {string} options.title - Notification title
   * @param {string} options.body - Notification message
   * @param {string} options.icon - Icon URL
   * @param {string} options.badge - Badge URL
   * @param {string} options.tag - Notification tag (for replacing)
   * @param {Object} options.data - Additional data
   * @param {Function} options.onClick - Click handler
   */
  async showNotification({ title, body, icon, badge, tag, data, onClick }) {
    if (!this.hasPermission()) {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      const options = {
        body,
        icon: icon || '/logo192.png',
        badge: badge || '/logo192.png',
        tag: tag || `notification-${Date.now()}`,
        data: data || {},
        requireInteraction: false,
        silent: false,
        vibrate: [200, 100, 200],
        timestamp: Date.now()
      };

      let notification;

      if (this.swRegistration) {
        // Use service worker for better control
        await this.swRegistration.showNotification(title, options);
        notification = await this.swRegistration.getNotifications({ tag: options.tag });
        notification = notification[0];
      } else {
        // Fallback to direct notification
        notification = new Notification(title, options);
      }

      if (onClick) {
        notification.onclick = (event) => {
          event.preventDefault();
          onClick(event);
          window.focus();
        };
      } else {
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }

      // Auto close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  /**
   * Show order status notification
   */
  async notifyOrderStatus(orderId, status, orderData = {}) {
    const statusMessages = {
      'Confirmed': `Order #${orderId} has been confirmed!`,
      'Processing Item': `Order #${orderId} is being processed.`,
      'Packed': `Order #${orderId} has been packed and ready to ship!`,
      'Handed to Courier': `Order #${orderId} has been handed to courier.`,
      'Shipment In Transit': `Order #${orderId} is on the way!`,
      'Delivery Completed': `Order #${orderId} has been delivered!`,
      'Cancelled': `Order #${orderId} has been cancelled.`,
      'Returned': `Return request for Order #${orderId} has been processed.`
    };

    const message = statusMessages[status] || `Order #${orderId} status updated to ${status}`;

    await this.showNotification({
      title: 'Order Update',
      body: message,
      icon: '/logo192.png',
      tag: `order-${orderId}`,
      data: { orderId, status, type: 'order_status' },
      onClick: () => {
        window.location.href = `/my-orders`;
      }
    });
  }

  /**
   * Show promotional notification
   */
  async notifyPromotion(title, message, promotionId, url = '/') {
    await this.showNotification({
      title: title || 'Special Offer!',
      body: message,
      icon: '/logo192.png',
      tag: `promotion-${promotionId}`,
      data: { promotionId, type: 'promotion' },
      onClick: () => {
        window.location.href = url;
      }
    });
  }

  /**
   * Show payment notification
   */
  async notifyPayment(orderId, status, amount) {
    const messages = {
      'Paid': `Payment of ₹${amount} for Order #${orderId} confirmed!`,
      'Failed': `Payment for Order #${orderId} failed. Please try again.`,
      'Refunded': `Refund of ₹${amount} for Order #${orderId} has been processed.`
    };

    await this.showNotification({
      title: 'Payment Update',
      body: messages[status] || `Payment status for Order #${orderId}: ${status}`,
      icon: '/logo192.png',
      tag: `payment-${orderId}`,
      data: { orderId, status, amount, type: 'payment' },
      onClick: () => {
        window.location.href = `/my-orders`;
      }
    });
  }

  /**
   * Show shipping notification
   */
  async notifyShipping(orderId, status, trackingNumber) {
    await this.showNotification({
      title: 'Shipping Update',
      body: `Your order #${orderId} ${status}. ${trackingNumber ? `Tracking: ${trackingNumber}` : ''}`,
      icon: '/logo192.png',
      tag: `shipping-${orderId}`,
      data: { orderId, status, trackingNumber, type: 'shipping' },
      onClick: () => {
        window.location.href = `/my-orders`;
      }
    });
  }

  /**
   * Show return notification
   */
  async notifyReturn(orderId, status) {
    const messages = {
      'Initiated': `Return request for Order #${orderId} has been initiated.`,
      'Approved': `Return request for Order #${orderId} has been approved. Pickup scheduled.`,
      'Pickup Scheduled': `Pickup for Order #${orderId} return has been scheduled.`,
      'Pickup Completed': `Pickup for Order #${orderId} return completed. Refund processing.`,
      'Refund Processing': `Refund for Order #${orderId} is being processed.`,
      'Refunded': `Refund for Order #${orderId} has been processed successfully.`
    };

    await this.showNotification({
      title: 'Return Update',
      body: messages[status] || `Return status for Order #${orderId}: ${status}`,
      icon: '/logo192.png',
      tag: `return-${orderId}`,
      data: { orderId, status, type: 'return' },
      onClick: () => {
        window.location.href = `/my-orders`;
      }
    });
  }

  /**
   * Clear all notifications
   */
  async clearAllNotifications() {
    if (this.swRegistration) {
      const notifications = await this.swRegistration.getNotifications();
      notifications.forEach(notification => notification.close());
    }
  }

  /**
   * Get notification count
   */
  async getNotificationCount() {
    if (this.swRegistration) {
      const notifications = await this.swRegistration.getNotifications();
      return notifications.length;
    }
    return 0;
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;

